import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

// Adaptive polling intervals based on nearest bus arrival time
const INTERVAL_FAR = 45000;      // > 10 min: 45 seconds
const INTERVAL_MEDIUM = 30000;   // 5-10 min: 30 seconds
const INTERVAL_CLOSE = 15000;    // < 5 min: 15 seconds

/**
 * Calculate adaptive refresh interval based on nearest bus arrival time
 * @param {Array} buses - Array of approaching buses
 * @returns {number} - Refresh interval in milliseconds
 */
const calculateAdaptiveInterval = (buses) => {
  if (!buses || buses.length === 0) {
    return INTERVAL_FAR; // No buses, check less frequently
  }

  // Find the minimum arrival time
  const minTime = Math.min(
    ...buses
      .map(b => parseInt(b.timeInMinutes) || Infinity)
      .filter(t => !isNaN(t) && t > 0)
  );

  if (minTime === Infinity) {
    return INTERVAL_FAR;
  }

  if (minTime < 5) {
    return INTERVAL_CLOSE;  // Bus very close, check frequently
  }

  if (minTime <= 10) {
    return INTERVAL_MEDIUM; // Bus approaching, moderate frequency
  }

  return INTERVAL_FAR;       // Bus far away, check less frequently
};

/**
 * Custom hook for fetching and managing approaching buses data
 * with adaptive polling based on bus proximity
 *
 * @param {string} stopNumber - The bus stop number to fetch buses for
 * @param {Object} options - Configuration options
 * @param {boolean} options.adaptivePolling - Enable adaptive polling intervals (default: true)
 * @param {number} options.refreshInterval - Fixed refresh interval if adaptive is disabled (default: 15000)
 * @param {boolean} options.pauseOnHidden - Pause refresh when tab is hidden (default: true)
 * @returns {Object} { buses, loading, error, lastUpdated, refresh, currentInterval }
 */
const useApproachingBuses = (stopNumber, options = {}) => {
  const {
    adaptivePolling = true,
    refreshInterval = 15000,
    pauseOnHidden = true
  } = options;

  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currentInterval, setCurrentInterval] = useState(refreshInterval);
  const intervalRef = useRef(null);

  const fetchBuses = useCallback(async () => {
    if (!stopNumber) {
      setBuses([]);
      setLoading(false);
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/api/BusStops/approaching-buses/${stopNumber}`);
      const newBuses = response.data || [];
      setBuses(newBuses);
      setLastUpdated(new Date());
      return newBuses;
    } catch (err) {
      console.error('Error fetching approaching buses:', err);
      setError(err.response?.data?.error || 'Failed to fetch approaching buses');
      setBuses([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [stopNumber]);

  // Initial fetch and adaptive auto-refresh
  useEffect(() => {
    const startPolling = async () => {
      const buses = await fetchBuses();

      // Clear existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Calculate next interval
      const nextInterval = adaptivePolling
        ? calculateAdaptiveInterval(buses)
        : refreshInterval;

      setCurrentInterval(nextInterval);

      // Set up recursive polling with adaptive interval
      const poll = async () => {
        const updatedBuses = await fetchBuses();
        const newInterval = adaptivePolling
          ? calculateAdaptiveInterval(updatedBuses)
          : refreshInterval;

        // If interval changed, restart with new interval
        if (newInterval !== intervalRef.currentInterval) {
          clearInterval(intervalRef.current);
          intervalRef.currentInterval = newInterval;
          setCurrentInterval(newInterval);
          intervalRef.current = setInterval(poll, newInterval);
        }
      };

      intervalRef.currentInterval = nextInterval;
      intervalRef.current = setInterval(poll, nextInterval);
    };

    startPolling();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchBuses, adaptivePolling, refreshInterval]);

  // Pause/resume when tab visibility changes
  useEffect(() => {
    if (!pauseOnHidden) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // Just fetch immediately when tab becomes visible
        // The main polling effect will handle interval adjustments
        await fetchBuses();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchBuses, pauseOnHidden]);

  return {
    buses,
    loading,
    error,
    lastUpdated,
    refresh: fetchBuses,
    currentInterval // Expose current interval for debugging/display
  };
};

export default useApproachingBuses;
